/**
 * WYSIWYG Execution Detail Page
 *
 * Full detail view for a single WYSIWYG execution: plan, graph, tasks, telemetry.
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import {
  ArrowLeftIcon,
  ClockIcon,
  SparklesIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { wysiwygStore } from '@/stores/WYSIWYGStore'

type Tab = 'plan' | 'tasks' | 'telemetry'

const WYSIWYGExecutionPage = observer(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('plan')
  const { selectedExecution: exec, isLoading, error } = wysiwygStore

  useEffect(() => {
    if (id) {
      wysiwygStore.fetchExecutionDetail(id)
    }
  }, [id])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading execution...</p>
      </div>
    )
  }

  if (error || !exec) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/wysiwyg')}>
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> Back
        </Button>
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">{error || 'Execution not found'}</p>
          <p className="text-xs text-muted-foreground mt-2">
            Execution history persistence will be available in a future update.
          </p>
        </Card>
      </div>
    )
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'plan', label: 'Plan' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'telemetry', label: 'Telemetry' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/wysiwyg')}>
          <ArrowLeftIcon className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-foreground">
            Execution {exec.id.substring(0, 8)}
          </h1>
          <p className="text-sm text-muted-foreground truncate max-w-lg">
            {exec.prompt}
          </p>
        </div>
        {exec.telemetry && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" />
              {(exec.telemetry.total_duration_ms / 1000).toFixed(1)}s
            </span>
            <span className="flex items-center gap-1">
              <CurrencyDollarIcon className="w-4 h-4" />
              ${exec.telemetry.total_cost_usd.toFixed(3)}
            </span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-violet-600 text-violet-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'plan' && exec.plan && (
        <Card className="p-4 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Execution Plan</h3>
          {exec.plan.remote_tasks?.length > 0 ? (
            <div className="space-y-2">
              {exec.plan.remote_tasks.map((task) => (
                <div key={task.task_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  <Badge variant="primary">{task.specialist}</Badge>
                  <span className="text-sm">{task.action_type}</span>
                  <span className="text-xs text-muted-foreground">
                    {task.strategy} {task.instance_count > 1 ? `x${task.instance_count}` : ''}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {task.duration_ms}ms | {task.quality_score?.toFixed(1)}/10
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No plan data available</p>
          )}
        </Card>
      )}

      {activeTab === 'tasks' && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Task Details</h3>
          <p className="text-sm text-muted-foreground">
            Detailed task breakdown will be available when execution persistence is implemented.
          </p>
        </Card>
      )}

      {activeTab === 'telemetry' && exec.telemetry && (
        <Card className="p-4 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Telemetry</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Duration</p>
              <p className="text-lg font-bold">{(exec.telemetry.total_duration_ms / 1000).toFixed(1)}s</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Tokens</p>
              <p className="text-lg font-bold">{exec.telemetry.total_tokens.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Cost</p>
              <p className="text-lg font-bold">${exec.telemetry.total_cost_usd.toFixed(4)}</p>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
})

export default WYSIWYGExecutionPage
